import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ManagedLoginService } from './managed-login.service';

export interface IUser {
  email: string;
  password: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private _auth: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private readonly managedLoginService: ManagedLoginService) {
    Auth.configure({
      Auth: environment.cognito,
    });
  }

  /**
   * Signs the user out from both Amplify and ManagedLogin sessions.
   *
   * @returns Promise resolving when signout is complete.
   */
  public signOut(): Promise<any> {
    this.managedLoginService.signOut();
    return Auth.signOut()
      .catch(() => {})
      .then(() => {
        this._auth.next(false);
      });
  }

  /**
   * Checks whether there is an active authenticated session via Amplify or ManagedLogin.
   *
   * @returns Promise resolving to boolean indicating authentication status.
   */
  public isAuthenticated(): Promise<boolean> {
    if (this._auth.value) {
      return Promise.resolve(true);
    }

    const decodedToken = this.managedLoginService.decodeToken();
    if (decodedToken && !this.managedLoginService.isTokenExpired()) {
      const userGroups = decodedToken['cognito:groups'] || [];
      if (
        userGroups.includes('ADMIN_AYB') ||
        userGroups.includes('ACCESS_DEV')
      ) {
        return Promise.resolve(true);
      }
    }

    return this.getUser()
      .then((userPayload: any) => {
        if (userPayload) {
          return true;
        }
        return false;
      })
      .catch(() => false)
      .then((userAuthenticated) => {
        if (userAuthenticated) {
          return this.getRol().then((rolValue) => {
            if (
              rolValue &&
              (rolValue.includes('ADMIN_AYB') ||
                rolValue.includes('ACCESS_DEV'))
            ) {
              return true;
            }
            return false;
          });
        }
        return false;
      });
  }

  /**
   * Retrieves user profile details from Amplify Auth or decoded Cognito JWT token.
   *
   * @returns Promise resolving to user profile object.
   */
  public getUser(): Promise<any> {
    return Auth.currentUserInfo()
      .then((userInfo) => {
        if (userInfo) {
          return userInfo;
        }
        const decodedJwt = this.managedLoginService.decodeToken();
        if (decodedJwt && decodedJwt.email) {
          return {
            username: decodedJwt.email,
            email: decodedJwt.email,
            attributes: {
              email: decodedJwt.email,
              sub: decodedJwt.sub,
              middle_name: decodedJwt.middle_name,
              family_name: decodedJwt.family_name,
            },
          };
        }
        return null;
      })
      .catch(() => {
        const decodedJwt = this.managedLoginService.decodeToken();
        if (decodedJwt && decodedJwt.email) {
          return {
            username: decodedJwt.email,
            email: decodedJwt.email,
            attributes: {
              email: decodedJwt.email,
              sub: decodedJwt.sub,
              middle_name: decodedJwt.middle_name,
              family_name: decodedJwt.family_name,
            },
          };
        }
        return null;
      });
  }

  /**
   * Retrieves the authenticated user entity with tokens and attributes.
   *
   * @returns Promise resolving to the authenticated user object.
   */
  public getAuthenticatedUser(): Promise<any> {
    return Auth.currentAuthenticatedUser().catch(() => {
      const decodedJwt = this.managedLoginService.decodeToken();
      if (decodedJwt && decodedJwt.email) {
        return {
          username: decodedJwt.email,
          attributes: {
            email: decodedJwt.email,
            sub: decodedJwt.sub,
            middle_name: decodedJwt.middle_name,
            family_name: decodedJwt.family_name,
          },
          signInUserSession: {
            idToken: {
              payload: decodedJwt,
              jwtToken: window.localStorage.getItem('id_token'),
            },
          },
        };
      }
      throw new Error('No authenticated user session found in AWS Cognito');
    });
  }

  /**
   * Retrieves the Cognito user groups assigned to the authenticated user.
   *
   * @returns Promise resolving to array of role group strings.
   */
  public getRol(): Promise<any> {
    return this.getAuthenticatedUser()
      .then((userEntity) => {
        return (
          userEntity?.signInUserSession?.idToken?.payload?.['cognito:groups'] ||
          userEntity?.attributes?.['cognito:groups'] ||
          []
        );
      })
      .catch(() => {
        const decodedJwt = this.managedLoginService.decodeToken();
        return decodedJwt?.['cognito:groups'] || [];
      });
  }

  /**
   * Retrieves active Cognito token session.
   *
   * @returns Promise resolving to Cognito session object.
   */
  public getSession(): Promise<any> {
    return Auth.currentSession().catch(() => {
      const decodedJwt = this.managedLoginService.decodeToken();
      const rawIdToken = window.localStorage.getItem('id_token');
      if (decodedJwt && rawIdToken) {
        return {
          getIdToken: () => ({
            decodePayload: () => decodedJwt,
            getJwtToken: () => rawIdToken,
          }),
          idToken: {
            payload: decodedJwt,
            jwtToken: rawIdToken,
          },
        };
      }
      throw new Error('No active session found in AWS Cognito');
    });
  }

  /**
   * Resolves the authenticated user email from the active Cognito session or decoded JWT token.
   *
   * @returns Promise resolving to the authenticated user's email string.
   * @throws Error when no active Cognito session or valid email exists.
   */
  public async getAuthenticatedUserEmail(): Promise<string> {
    const decodedToken = this.managedLoginService.decodeToken();
    if (
      decodedToken?.email &&
      typeof decodedToken.email === 'string' &&
      decodedToken.email.trim()
    ) {
      return decodedToken.email.trim();
    }

    try {
      const userPayload = await this.getUser();
      const resolvedEmail =
        userPayload?.attributes?.email || userPayload?.email;
      if (
        resolvedEmail &&
        typeof resolvedEmail === 'string' &&
        resolvedEmail.trim()
      ) {
        return resolvedEmail.trim();
      }
    } catch {
      // Fallback to getAuthenticatedUser
    }

    try {
      const authenticatedUser = await this.getAuthenticatedUser();
      const resolvedEmail =
        authenticatedUser?.attributes?.email ||
        authenticatedUser?.signInUserSession?.idToken?.payload?.email;
      if (
        resolvedEmail &&
        typeof resolvedEmail === 'string' &&
        resolvedEmail.trim()
      ) {
        return resolvedEmail.trim();
      }
    } catch {
      // Session not found
    }

    throw new Error(
      'Usuario no identificado. No se encontró una sesión activa  con un correo electrónico válido.',
    );
  }
}
